// src/components/common/GraiaAudioPlayer.tsx

import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import './GraiaAudioPlayer.css';

interface GraiaAudioPlayerProps {
    audioUrl: string;
    compact?: boolean;
    studyMode?: boolean;
}

export const GraiaAudioPlayer: React.FC<GraiaAudioPlayerProps> = ({
    audioUrl,
    compact = false,
    studyMode = false
}) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(1);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const updateTime = () => setCurrentTime(audio.currentTime);
        const updateDuration = () => setDuration(audio.duration);
        const handleEnded = () => setIsPlaying(false);

        audio.addEventListener('timeupdate', updateTime);
        audio.addEventListener('loadedmetadata', updateDuration);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('timeupdate', updateTime);
            audio.removeEventListener('loadedmetadata', updateDuration);
            audio.removeEventListener('ended', handleEnded);
        };
    }, []);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play();
        }
        setIsPlaying(!isPlaying);
    };

    const toggleMute = () => {
        const audio = audioRef.current;
        if (!audio) return;

        audio.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const audio = audioRef.current;
        if (!audio) return;

        const newVolume = parseFloat(e.target.value);
        audio.volume = newVolume;
        setVolume(newVolume);

        if (newVolume === 0) {
            setIsMuted(true);
        } else if (isMuted) {
            setIsMuted(false);
        }
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const audio = audioRef.current;
        if (!audio) return;

        const newTime = parseFloat(e.target.value);
        audio.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const formatTime = (time: number) => {
        if (isNaN(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <div className={`graia-audio-player ${compact ? 'compact' : ''}`}>
            <audio ref={audioRef} src={audioUrl} preload="metadata" />

            {/* ✅ Play/Pause */}
            <button
                className="graia-audio-play-btn"
                onClick={togglePlay}
                aria-label={isPlaying ? 'Pausar' : 'Reproduzir'}
            >
                {isPlaying ? (
                    <Pause size={20} />
                ) : (
                    <Play size={20} style={{ transform: 'translateX(1.5px)' }} />
                )}
            </button>

            {/* ✅ Progresso + Tempo */}
            <div className="graia-audio-progress-container">
                <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    value={currentTime}
                    onChange={handleSeek}
                    className="graia-audio-progress-bar"
                    style={{
                        background: `linear-gradient(to right, #2F7130 0%, #2F7130 ${progress}%, rgba(47, 113, 48, 0.15) ${progress}%, rgba(47, 113, 48, 0.15) 100%)`
                    }}
                />
                {!studyMode && (
                    <div className="graia-audio-time">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                    </div>
                )}

                {studyMode && (
                    <div className="graia-audio-time single">
                        <span>{formatTime(currentTime)}</span>
                    </div>
                )}
            </div>

            {/* ✅ Volume (só se não for compacto) */}
            {!compact && (
                <div className="graia-audio-volume-container">
                    <button
                        className="graia-audio-volume-btn"
                        onClick={toggleMute}
                        aria-label={isMuted ? 'Ativar som' : 'Silenciar'}
                    >
                        {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                    </button>

                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={isMuted ? 0 : volume}
                        onChange={handleVolumeChange}
                        className="graia-audio-volume-slider"
                        style={{
                            background: `linear-gradient(to right, #2F7130 0%, #2F7130 ${(isMuted ? 0 : volume) * 100}%, rgba(47, 113, 48, 0.15) ${(isMuted ? 0 : volume) * 100}%, rgba(47, 113, 48, 0.15) 100%)`
                        }}
                    />
                </div>
            )}
        </div>
    );
};